// What a bulk move promises when one of twenty is refused.
//
// ── THE CONTRACT, CHOSEN EXPLICITLY ─────────────────────────────────────────────
// PARTIAL PROCESSING WITH A RESULT PER ALLOCATION. Not all-or-nothing.
//
// Atomicity across N allocations would need one endpoint that takes the whole batch and holds a
// transaction across every deployment-rule check — a new backend layer this sprint is explicitly not
// building, and one that would make a single refused day silently undo nineteen good moves the
// planner had already seen land.
//
// So each day is its own guarded write, and the result says exactly which ones happened. The rule
// that makes this safe rather than sloppy: THERE IS NO SILENT PARTIAL STATE. Every outcome is
// counted, the refused ones are named, and undo covers precisely the ones that succeeded.
//
// The alternative — reporting only "3 geweigerd" and leaving the planner to work out which — is the
// failure mode this file exists to prevent.
export interface BulkOutcome<T> {
  item: T;
  ok: boolean;
  /** The server's sentence, for the ones that failed. Never invented. */
  reason?: string;
  /** How to put this one back. Only present when it succeeded. */
  undo?: () => Promise<void>;
}

export interface BulkResult<T> {
  done: BulkOutcome<T>[];
  failed: BulkOutcome<T>[];
  /** Undo everything that actually happened, newest first. */
  undo?: () => Promise<void>;
}

/**
 * Run one guarded write per item, sequentially, collecting every outcome.
 *
 * SEQUENTIAL, not `Promise.all`: these are guarded writes against rows that read each other — the
 * deployment rules look at the other assignments on the same workpackage while deciding. Two parallel
 * writes to one job can both observe a state neither of them ends in.
 */
export async function bulk<T>(
  items: T[],
  run: (item: T) => Promise<(() => Promise<void>) | void>,
): Promise<BulkResult<T>> {
  const done: BulkOutcome<T>[] = [];
  const failed: BulkOutcome<T>[] = [];
  for (const item of items) {
    try {
      const undo = await run(item);
      done.push({ item, ok: true, undo: typeof undo === 'function' ? undo : undefined });
    } catch (e) {
      failed.push({ item, ok: false, reason: (e as Error).message });
    }
  }
  const undoable = done.filter((d) => d.undo);
  return {
    done, failed,
    undo: undoable.length
      ? async () => { for (const d of [...undoable].reverse()) await d.undo!(); }
      : undefined,
  };
}

/**
 * One sentence a planner can act on.
 *
 * Names the refusals rather than counting them: "3 geweigerd" tells somebody there is a problem and
 * nothing about where it is.
 */
export function bulkMessage(r: BulkResult<unknown>, describe: (item: unknown) => string): string {
  const n = r.done.length;
  if (r.failed.length === 0) return `${n} ${n === 1 ? 'dag' : 'dagen'} verplaatst.`;
  const namen = r.failed.slice(0, 3).map((f) => describe(f.item)).join(', ');
  const rest = r.failed.length > 3 ? ` en ${r.failed.length - 3} andere` : '';
  return n === 0
    ? `Niets verplaatst — ${namen}${rest} werd geweigerd: ${r.failed[0].reason}`
    : `${n} verplaatst, ${r.failed.length} geweigerd (${namen}${rest}): ${r.failed[0].reason}`;
}
